/**
 * Branding context - loads the header logo once at boot and hands it to every
 * place that draws the brand (header, login screen, access-denied screen).
 *
 * Mounted ABOVE the session check so the login screen gets it too; the read is
 * the public endpoint for the same reason. `apply()` lets the settings form push
 * the just-saved value in, so the header changes without a page reload.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { EMPTY_BRANDING, getPublicBranding, type CrmBranding } from "../lib/branding";

interface BrandingValue {
  branding: CrmBranding;
  apply: (b: CrmBranding) => void;
}

const BrandingContext = createContext<BrandingValue>({ branding: EMPTY_BRANDING, apply: () => {} });

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<CrmBranding>(EMPTY_BRANDING);

  useEffect(() => {
    let alive = true;
    // A failed read is not worth surfacing: the built-in logo is the fallback.
    getPublicBranding()
      .then((b) => { if (alive) setBranding({ ...EMPTY_BRANDING, ...b }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const apply = useCallback((b: CrmBranding) => setBranding({ ...EMPTY_BRANDING, ...b }), []);
  const value = useMemo(() => ({ branding, apply }), [branding, apply]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingValue {
  return useContext(BrandingContext);
}
